/**
 * WH-RESTOCK-01/03 (Seth 2026-09-29): the warehouse Restock list.
 *
 * Flow (v3 — Seth: "something seems off… a lot of clicks right?"):
 * add lines → Make the POs → land STRAIGHT IN THE FULL PO EDITOR (one PO) or
 * one button per PO (several). No list → preview → sidebar → Edit chain.
 * Terms come from the vendor's record and the date is today, so most drafts
 * have nothing left to type — review, Save, print or call it in.
 *
 * For a Job (slice 3): any line can attach to a tech's call — the tech's
 * FLAGGED part tickets surface first ("Wayne already flagged: inducer motor")
 * so the warehouse joins the existing ticket instead of duplicating it. Job
 * lines get their own PO per vendor+call, stamped with dispatch/ticket/
 * customer; stock lines stay on the office PO.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PackagePlus, Store, ChevronDown, Minus, Plus, X, Check, Sparkles, Loader2, ArrowRight, Wrench,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { InfoTip } from "@/components/InfoTip";
import { TeamMemberSelect } from "@/components/TeamMemberSelect";
import { PricebookPicker } from "@/features/items/PricebookPicker";
import { useVendors } from "@/features/items/items";
import { useCreateNativePo, SEDGWICK_SHIP_TO } from "@/features/purchase-orders/po";
import {
  useRestockLines, useAddRestockLine, useUpdateRestockLine, useMarkLinesOrdered,
  useTechOpenPartTickets, useTechRecentCalls,
  type RestockLine, type TechPartTicket, type TechRecentCall,
} from "./restock";

const qtyText = (n: number) => (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const money = (n: number) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dayText = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

/** Tap the name to rename it — the free-text ("MAT") lines especially. */
function EditableDesc({ line }: { line: RestockLine }) {
  const upd = useUpdateRestockLine();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(line.description);
  useEffect(() => { setV(line.description); }, [line.description]);
  const commit = () => {
    setEditing(false);
    const t = v.trim();
    if (t && t !== line.description) upd.mutate({ id: line.id, description: t });
    else setV(line.description);
  };
  if (editing) {
    return (
      <input
        autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setV(line.description); setEditing(false); }
        }}
        className="w-full rounded-lg border border-brand-300 bg-white px-2 py-1 text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} title="Tap to rename"
      className="block w-full text-left text-[15px] font-semibold leading-snug text-slate-900 hover:text-brand-700">
      {line.description}
    </button>
  );
}

/** Inline vendor chooser — opens under the line, filters the full vendor list. */
function VendorPickRow({ line, onDone }: { line: RestockLine; onDone: () => void }) {
  const { data: vend } = useVendors();
  const upd = useUpdateRestockLine();
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = vend?.list ?? [];
    const f = s ? all.filter((v) => (v.name ?? v.code).toLowerCase().includes(s) || v.code.toLowerCase().includes(s)) : all;
    return f.slice(0, 8);
  }, [vend, q]);
  return (
    <div className="mt-2 rounded-xl border border-hairline bg-slate-50 p-2.5">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors…"
        className="w-full rounded-lg border border-hairline bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {list.map((v) => (
          <button key={v.code} type="button"
            onClick={() => { upd.mutate({ id: line.id, vendor_code: v.code, vendor_name: v.name ?? v.code, vendor_reason: null }); onDone(); }}
            className="rounded-lg bg-white px-2.5 py-1.5 text-[13px] font-semibold text-slate-700 ring-1 ring-hairline hover:bg-brand-50 hover:text-brand-700">
            {v.name ?? v.code}
          </button>
        ))}
        {line.vendor_code && (
          <button type="button"
            onClick={() => { upd.mutate({ id: line.id, vendor_code: null, vendor_name: null, vendor_reason: null }); onDone(); }}
            className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-rose-600 hover:bg-rose-50">
            Clear
          </button>
        )}
        <button type="button" onClick={onDone} className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-slate-500 hover:bg-slate-100">
          Close
        </button>
      </div>
    </div>
  );
}

/** For a Job: tech → their flagged part tickets FIRST → their recent calls. */
function ForJobRow({ line, onDone }: { line: RestockLine; onDone: () => void }) {
  const upd = useUpdateRestockLine();
  const [empno, setEmpno] = useState<string | null>(null);
  const [first, setFirst] = useState<string>("");
  const tickets = useTechOpenPartTickets(empno);
  const calls = useTechRecentCalls(empno);

  const attachTicket = (t: TechPartTicket) => {
    upd.mutate({
      id: line.id,
      ticket_id: t.id,
      dispatch_no: t.origin_dispatch ?? (t.dispatch_no != null ? String(t.dispatch_no) : null),
      dispatch_label: [first, t.customer_name].filter(Boolean).join(" · "),
      custno: t.custno, locno: t.locno,
    });
    onDone();
  };
  const attachCall = (c: TechRecentCall) => {
    upd.mutate({
      id: line.id,
      ticket_id: null,
      dispatch_no: c.dispatch,
      dispatch_label: [first, c.customer].filter(Boolean).join(" · "),
      custno: c.custno, locno: c.locno,
    });
    onDone();
  };

  return (
    <div className="mt-2 rounded-xl border border-hairline bg-slate-50 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <TeamMemberSelect
          value={empno}
          includeBlank
          blankLabel="Whose call?"
          onChange={(e, name) => { setEmpno(e); setFirst((name ?? "").split(/\s+/)[0] ?? ""); }}
          className="rounded-lg border border-hairline bg-white px-2 py-1.5 text-[13px]"
        />
        {line.dispatch_no && (
          <button type="button"
            onClick={() => { upd.mutate({ id: line.id, ticket_id: null, dispatch_no: null, dispatch_label: null, custno: null, locno: null }); onDone(); }}
            className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-rose-600 hover:bg-rose-50">
            Back to stock
          </button>
        )}
        <button type="button" onClick={onDone} className="ml-auto rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-slate-500 hover:bg-slate-100">
          Close
        </button>
      </div>

      {empno && (
        <div className="mt-2 space-y-1.5">
          {(tickets.data ?? []).map((t) => (
            <button key={t.id} type="button" onClick={() => attachTicket(t)}
              className="flex w-full items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left hover:bg-amber-100">
              <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span className="min-w-0">
                <span className="block text-[13.5px] font-bold text-amber-900">
                  Already flagged: {t.part_number || t.summary}
                </span>
                <span className="block truncate text-[12.5px] text-amber-800/80">
                  {t.customer_name}{t.address ? ` · ${t.address}` : ""}
                </span>
              </span>
            </button>
          ))}
          {(calls.data ?? []).map((c) => (
            <button key={c.dispatch} type="button" onClick={() => attachCall(c)}
              className="flex w-full items-baseline gap-2 rounded-xl bg-white px-3 py-2 text-left ring-1 ring-hairline hover:bg-brand-50">
              <span className="text-[13.5px] font-semibold text-slate-800">{c.customer}</span>
              <span className="shrink-0 text-[12px] font-semibold text-slate-400">{dayText(c.date)}</span>
              {c.note && <span className="min-w-0 truncate text-[12px] text-slate-400">{c.note}</span>}
            </button>
          ))}
          {tickets.data && calls.data && tickets.data.length === 0 && calls.data.length === 0 && (
            <p className="px-1 py-1 text-[12.5px] text-slate-400">No calls in the last 2 weeks.</p>
          )}
          {(tickets.isLoading || calls.isLoading) && (
            <p className="flex items-center gap-1.5 px-1 py-1 text-[12.5px] font-semibold text-slate-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function RestockPane() {
  const { data: lines, isLoading } = useRestockLines();
  const { data: vend } = useVendors();
  const add = useAddRestockLine();
  const upd = useUpdateRestockLine();
  const markOrdered = useMarkLinesOrdered();
  const createPo = useCreateNativePo();
  const navigate = useNavigate();
  const [chipOpen, setChipOpen] = useState<string | null>(null);
  const [jobOpen, setJobOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<{ id: string; po: string; vendor: string; job: string | null; lines: number }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const open = lines ?? [];
  const { groups, noVendor } = useMemo(() => {
    const g = new Map<string, RestockLine[]>();
    const nv: RestockLine[] = [];
    for (const l of open) {
      if (!l.vendor_code) { nv.push(l); continue; }
      const key = `${l.vendor_code}|${l.dispatch_no ?? ""}`;
      const arr = g.get(key) ?? [];
      arr.push(l);
      g.set(key, arr);
    }
    // Stock POs first, then job POs, each alphabetical by vendor.
    const list = [...g.values()].sort((a, b) => {
      const aj = a[0].dispatch_no ? 1 : 0;
      const bj = b[0].dispatch_no ? 1 : 0;
      if (aj !== bj) return aj - bj;
      return (a[0].vendor_name ?? a[0].vendor_code ?? "").localeCompare(b[0].vendor_name ?? b[0].vendor_code ?? "");
    });
    return { groups: list, noVendor: nv };
  }, [open]);

  const makePos = async () => {
    setBusy(true); setErr(null); setMade(null);
    const out: { id: string; po: string; vendor: string; job: string | null; lines: number }[] = [];
    try {
      // Vendor terms come along so the draft is usually complete as-is.
      const codes = [...new Set(groups.map((ls) => ls[0].vendor_code as string))];
      const termsBy = new Map<string, string | null>();
      if (codes.length) {
        const { data: vr } = await supabase.from("mirror_vendor").select("vendor_code, terms").in("vendor_code", codes);
        for (const v of (vr ?? []) as { vendor_code: string; terms: string | null }[]) termsBy.set(v.vendor_code, v.terms);
      }
      const today = new Date().toISOString().slice(0, 10);
      for (const ls of groups) {
        const f = ls[0];
        const code = f.vendor_code as string;
        const po = await createPo.mutateAsync({
          vendor_code: code,
          terms: termsBy.get(code) ?? null,
          date_requested: today,
          ship_to_addr: SEDGWICK_SHIP_TO,
          memo: f.dispatch_no
            ? `Part for ${f.dispatch_label ?? `call ${f.dispatch_no}`} — from the Restock list`
            : "Stock restock — built from the warehouse Restock list",
          dispatch_no: f.dispatch_no ?? null,
          ticket_id: f.ticket_id ?? null,
          custno: f.custno ?? null,
          locno: f.locno ?? null,
          draft: true,
          lines: ls.map((l) => ({
            part_number: l.part_number,
            description: l.description,
            qty: Number(l.qty),
            unit_cost: Number(l.unit_cost ?? 0),
          })),
        });
        await markOrdered.mutateAsync({ ids: ls.map((l) => l.id), po_id: po.id });
        out.push({
          id: po.id,
          po: po.po_number ?? "—",
          vendor: f.vendor_name ?? (vend?.label(code) || code),
          job: f.dispatch_no ? (f.dispatch_label ?? f.dispatch_no) : null,
          lines: ls.length,
        });
      }
      // ONE PO → straight into the full editor. No detours.
      if (out.length === 1) {
        navigate(`/purchase-orders?edit=${out[0].id}`);
        return;
      }
      setMade(out);
    } catch {
      setErr("Something stopped partway — check Purchase Orders. Lines not on a PO are still below.");
      if (out.length > 0) setMade(out);
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ l }: { l: RestockLine }) => (
    <li className="rounded-2xl border border-hairline bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 items-center gap-1 rounded-xl bg-slate-100 p-1">
          <button type="button" disabled={Number(l.qty) <= 1}
            onClick={() => upd.mutate({ id: l.id, qty: Number(l.qty) - 1 })}
            className="grid h-9 w-9 place-items-center rounded-lg bg-white text-slate-600 shadow-sm disabled:opacity-30">
            <Minus className="h-4 w-4" strokeWidth={3} />
          </button>
          <span className="w-8 text-center text-[17px] font-extrabold tabular-nums">{qtyText(l.qty)}</span>
          <button type="button" onClick={() => upd.mutate({ id: l.id, qty: Number(l.qty) + 1 })}
            className="grid h-9 w-9 place-items-center rounded-lg bg-white text-slate-600 shadow-sm">
            <Plus className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>
        <div className="min-w-0 flex-1">
          <EditableDesc line={l} />
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            {l.part_number ?? "No part number"}
            {l.unit_cost != null && Number(l.unit_cost) > 0 && <> · {money(Number(l.unit_cost))} ea</>}
          </p>
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <button type="button" title={l.vendor_reason ?? undefined}
              onClick={() => { setJobOpen(null); setChipOpen(chipOpen === l.id ? null : l.id); }}
              className={cn(
                "inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-bold",
                l.vendor_code ? "bg-brand-50 text-brand-700" : "bg-amber-100 text-amber-800",
              )}>
              <Store className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 truncate">{l.vendor_name ?? (l.vendor_code ? (vend?.label(l.vendor_code) || l.vendor_code) : "Pick a Vendor")}</span>
              {l.vendor_reason && <Sparkles className="h-3 w-3 shrink-0 opacity-70" />}
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
            </button>
            <button type="button"
              onClick={() => { setChipOpen(null); setJobOpen(jobOpen === l.id ? null : l.id); }}
              className={cn(
                "inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-bold",
                l.dispatch_no ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-500",
              )}>
              <Wrench className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 truncate">{l.dispatch_no ? `For ${l.dispatch_label ?? l.dispatch_no}` : "For a Job?"}</span>
            </button>
          </span>
        </div>
        <button type="button" title="Remove"
          onClick={() => upd.mutate({ id: l.id, status: "cancelled" })}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-600">
          <X className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
      {chipOpen === l.id && <VendorPickRow line={l} onDone={() => setChipOpen(null)} />}
      {jobOpen === l.id && <ForJobRow line={l} onDone={() => setJobOpen(null)} />}
    </li>
  );

  const partCount = groups.reduce((n, ls) => n + ls.length, 0);

  return (
    <section className="rounded-2xl border border-hairline bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <PackagePlus className="h-5 w-5 text-brand-600" />
        <h2 className="text-[17px] font-bold text-slate-900">Restock</h2>
        {open.length > 0 && (
          <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[13px] font-bold text-brand-700">{open.length}</span>
        )}
        <InfoTip label="How Restock works">
          Add what's low; each line suggests the vendor we usually buy it from (tap the chip to
          change, tap the name to rename). For a Job attaches a line to a tech's call — their
          flagged part tickets show first. Make the POs cuts one draft per vendor (job lines get
          their own PO, stamped with the call) and opens it for review — terms and date are
          already filled. Nothing is placed until you save it there.
        </InfoTip>
      </div>

      <div className="mt-3">
        <PricebookPicker
          placeholder="Search the price book…"
          pickLabel="Add"
          showCost
          onPick={(it) => add.mutate({ item: it })}
          onNoMatch={(term) => add.mutate({ text: term })}
          noMatchLabel="Add as typed"
        />
        {add.isPending && (
          <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Adding…
          </p>
        )}
      </div>

      {made && made.length > 0 && (
        <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3.5">
          <p className="flex items-center gap-1.5 text-[14px] font-bold text-emerald-800">
            <Check className="h-4 w-4" strokeWidth={3} />
            {made.length} draft POs — open each to review
          </p>
          <ul className="mt-1.5 space-y-1">
            {made.map((m) => (
              <li key={m.id}>
                <button type="button" onClick={() => navigate(`/purchase-orders?edit=${m.id}`)}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[13px] font-bold text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-100">
                  <span className="tabular-nums">PO {m.po}</span>
                  <span className="min-w-0 truncate font-semibold">· {m.vendor}{m.job ? ` · ${m.job}` : ""} · {m.lines} {m.lines === 1 ? "line" : "lines"}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setMade(null)}
            className="mt-1.5 rounded-lg px-2 py-1 text-[12.5px] font-semibold text-emerald-700 hover:bg-emerald-100">
            Dismiss
          </button>
        </div>
      )}
      {err && <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-[13px] font-semibold text-rose-700">{err}</p>}

      {isLoading ? (
        <p className="mt-3 rounded-xl border border-hairline bg-white py-6 text-center text-sm text-slate-400">Loading…</p>
      ) : open.length === 0 ? (
        !made && (
          <p className="mt-3 rounded-xl border border-dashed border-hairline bg-slate-50/60 px-4 py-4 text-center text-[13px] text-slate-400">
            Nothing on the list.
          </p>
        )
      ) : (
        <>
          <ul className="mt-3 space-y-2">
            {open.map((l) => <Row key={l.id} l={l} />)}
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-hairline pt-3">
            <div className="min-w-0 flex-1 text-[13px] text-slate-500">
              {groups.length > 0 && (
                <span className="font-semibold text-slate-700">
                  {partCount} {partCount === 1 ? "part" : "parts"} · {groups.length} {groups.length === 1 ? "PO" : "POs"}
                </span>
              )}
              {noVendor.length > 0 && (
                <span className={cn("font-semibold text-amber-700", groups.length > 0 && "ml-2")}>
                  {noVendor.length} {noVendor.length === 1 ? "needs" : "need"} a vendor
                </span>
              )}
            </div>
            <button type="button" disabled={busy || groups.length === 0} onClick={() => void makePos()}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-[14px] font-bold text-white shadow-sm hover:bg-slate-800 disabled:opacity-40">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {groups.length === 1 ? "Make the PO" : "Make the POs"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
